import { FunctionComponent, useContext } from 'react';
import { NewsletterSignup, Environment, CommunityApp } from '@atlassian/learning-components';
import { css } from '@compiled/react';
import { UserContext } from 'context/UserContext';
import { sendTrackEvent, sendUIEvent, sendConversionOperationalEvent } from 'services/analytics';
import { buildConversionEventAttributes } from '../../../../analytics/campaign-context';
import {
  CONVERSION_FLOWS,
  CONVERSION_OPERATIONAL_EVENTS,
  CONVERSION_TYPES,
  SUBMIT_STATUS,
} from '../../../../analytics/constants';
import LOGGER from 'services/sentry/sentry';
import { useSmoothScrollToHash } from 'hooks/useSmoothScrollToHash';

const SCROLL_TARGET = 'subscribe';

export const getApiEnv = () => {
  switch (process.env.REACT_APP_ENVI) {
    case 'production':
      return Environment.PRODUCTION;
    case 'staging':
      return Environment.STAGING;
    default:
      return Environment.DEVELOPMENT;
  }
};

const onNewsletterClick = async (app: CommunityApp, atlassianId?: string) => {
  await sendUIEvent(
    `${app}Newsletter`, // eg. 'homeNewsletter' - indicates which area of the app it's sent from
    'button',
    'clicked',
    'communityNewsletterSignup',
    {},
    atlassianId,
  );
};

const onPrivacyPolicyClick = async (app: CommunityApp, atlassianId?: string) => {
  await sendUIEvent(`${app}Newsletter`, 'link', 'clicked', 'communityNewsletterPrivacyPolicy', {}, atlassianId);
};

const onNewsletterSignupSuccess = async (app: CommunityApp, atlassianId?: string) => {
  await sendTrackEvent(`${app}Newsletter`, 'userCommunityNewsletterSignup', 'succeeded', {}, atlassianId);

  // Operational (strictly-necessary, no identity): newsletter submit conversion.
  await sendConversionOperationalEvent(
    `${app}Newsletter`,
    'form',
    CONVERSION_OPERATIONAL_EVENTS.NEWSLETTER_SIGNUP_SUBMIT,
    buildConversionEventAttributes({
      eventName: CONVERSION_OPERATIONAL_EVENTS.NEWSLETTER_SIGNUP_SUBMIT,
      conversionType: CONVERSION_TYPES.NEWSLETTER_SIGNUP_SUBMIT,
      extra: {
        flow_name: CONVERSION_FLOWS.NEWSLETTER,
        submit_status: SUBMIT_STATUS.SUCCESS,
        placement: app,
      },
    }),
  );
};

const onNewsletterSignupError = async (app: CommunityApp, atlassianId?: string) => {
  await sendTrackEvent(`${app}Newsletter`, 'userCommunityNewsletterSignup', 'failed', {}, atlassianId);

  // Operational (strictly-necessary, no identity): newsletter submit failure.
  await sendConversionOperationalEvent(
    `${app}Newsletter`,
    'form',
    CONVERSION_OPERATIONAL_EVENTS.NEWSLETTER_SIGNUP_SUBMIT,
    buildConversionEventAttributes({
      eventName: CONVERSION_OPERATIONAL_EVENTS.NEWSLETTER_SIGNUP_SUBMIT,
      conversionType: CONVERSION_TYPES.NEWSLETTER_SIGNUP_SUBMIT,
      extra: {
        flow_name: CONVERSION_FLOWS.NEWSLETTER,
        submit_status: SUBMIT_STATUS.ERROR,
        placement: app,
      },
    }),
  );
};

type NewsletterProps = {
  app: CommunityApp;
};

const Newsletter: FunctionComponent<NewsletterProps> = ({ app }) => {
  const scrollTargetRef = useSmoothScrollToHash(`#${SCROLL_TARGET}`);

  const data = useContext(UserContext);
  const user = data?.user;

  const atlassianId = user?.account_id;

  const pageUrl = window.location.href;

  return (
    <div id={SCROLL_TARGET} css={containerStyle} ref={scrollTargetRef}>
      <NewsletterSignup
        atlassianId={atlassianId}
        environment={getApiEnv()}
        formUrl={pageUrl}
        eventHandlers={{
          onSubmit: async () => await onNewsletterClick(app, atlassianId),
          onSuccess: async () => await onNewsletterSignupSuccess(app, atlassianId),
          onPrivacyPolicyClick: async () => await onPrivacyPolicyClick(app, atlassianId),
          onError: async (error) => {
            LOGGER.error(error);
            await onNewsletterSignupError(app, atlassianId);
          },
        }}
      />
    </div>
  );
};

export default Newsletter;

const containerStyle = css({
  width: '100%',
  padding: '0 1rem',
});
